/* eslint-disable react/no-array-index-key */
import {localTimeZone} from '../../../constants/index.js'
import {DatePicker as ArkDatePicker, type DateValue} from '@ark-ui/react/date-picker'
import {Box, Button, Flex, HStack, IconButton, Input, Table, Text} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {useMemo, useState} from 'react'
import {LuChevronLeft, LuChevronRight} from 'react-icons/lu'
import {timestampToDateString} from '../../../utils/index.js'
import {Tolgee} from '../../../integrations/tolgee/index.js'

interface DatePickerSelectorProps {
  currentValue?: number[]
  onChange: (filters: number[]) => void
  rangeSelect: boolean
  open?: boolean
  minDate?: number
  maxDate?: number
}

export function DatePickerSelector({
  currentValue,
  onChange,
  rangeSelect,
  open,
  minDate,
  maxDate,
}: DatePickerSelectorProps) {
  const {t} = useTranslate('common')

  let min, max

  if (minDate) min = timestampToDateString(minDate)
  if (maxDate) max = timestampToDateString(maxDate)

  const currentDateValues = useMemo(() => {
    if (!currentValue || currentValue.length === 0) return undefined
    return currentValue.map(timestamp => timestampToDateString(timestamp))
  }, [currentValue])

  const initialTimes = useMemo(() => {
    if (!currentValue || currentValue.length === 0) {
      return {start: '00:00', end: '23:59'}
    }

    const start = getFormattedTime(currentValue[0])

    if (rangeSelect && currentValue.length > 1) {
      const end = getFormattedTime(currentValue[1])
      return {start, end}
    }

    return {start, end: '23:59'}
  }, [currentValue, rangeSelect])

  const [selectedDates, setSelectedDates] = useState<DateValue[]>(currentDateValues || [])
  const [startTime, setStartTime] = useState(initialTimes.start)
  const [endTime, setEndTime] = useState(initialTimes.end)

  const handleApply = () => {
    if (selectedDates.length === 0) return

    const timestamps = selectedDates.map((date, index) => {
      const timeString = index === 0 ? startTime : endTime

      return dateValueToTimestamp(date, timeString)
    })

    onChange(timestamps)
  }

  return (
    <ArkDatePicker.Root
      defaultValue={currentDateValues}
      selectionMode={rangeSelect ? 'range' : 'single'}
      onValueChange={e => setSelectedDates(e.value)}
      open={open}
      closeOnSelect={false}
      min={min}
      max={max}
      fixedWeeks
      positioning={{placement: 'bottom-end'}}
    >
      <Box bg="bg.panel" borderWidth="1px" borderRadius="lg" shadow="lg" p="4">
        <ArkDatePicker.View view="day">
          <ArkDatePicker.Context>
            {datePicker => (
              <>
                <ArkDatePicker.ViewControl asChild>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb="4">
                    <ArkDatePicker.PrevTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <LuChevronLeft />
                      </IconButton>
                    </ArkDatePicker.PrevTrigger>
                    <ArkDatePicker.ViewTrigger asChild>
                      <Button variant="ghost" size="sm" fontWeight="semibold">
                        <ArkDatePicker.RangeText />
                      </Button>
                    </ArkDatePicker.ViewTrigger>
                    <ArkDatePicker.NextTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <LuChevronRight />
                      </IconButton>
                    </ArkDatePicker.NextTrigger>
                  </Box>
                </ArkDatePicker.ViewControl>
                <ArkDatePicker.Table asChild>
                  <Table.Root size="sm">
                    <ArkDatePicker.TableHead asChild>
                      <Table.Header>
                        <ArkDatePicker.TableRow asChild>
                          <Table.Row>
                            {datePicker.weekDays.map((weekDay, id) => (
                              <ArkDatePicker.TableHeader key={`${id}`} asChild>
                                <Table.ColumnHeader textAlign="center" fontWeight="medium" color="fg.muted">
                                  {weekDay.short}
                                </Table.ColumnHeader>
                              </ArkDatePicker.TableHeader>
                            ))}
                          </Table.Row>
                        </ArkDatePicker.TableRow>
                      </Table.Header>
                    </ArkDatePicker.TableHead>
                    <ArkDatePicker.TableBody asChild>
                      <Table.Body>
                        {datePicker.weeks.map((week, id) => (
                          <ArkDatePicker.TableRow key={`${id}`} asChild>
                            <Table.Row>
                              {week.map((day, id) => (
                                <ArkDatePicker.TableCell key={`${id}`} value={day} asChild>
                                  <Table.Cell textAlign="center" p="0.5">
                                    <ArkDatePicker.TableCellTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        w="full"
                                        h="8"
                                        p="0.5"
                                        fontSize="sm"
                                        _selected={{
                                          bg: 'colorPalette.solid',
                                          color: 'white',
                                        }}
                                        _disabled={{
                                          opacity: 0.4,
                                          cursor: 'not-allowed',
                                        }}
                                        css={{
                                          '&[data-in-range]': {
                                            bg: 'colorPalette.muted',
                                            color: 'colorPalette.fg',
                                          },
                                        }}
                                      >
                                        {day.day}
                                      </Button>
                                    </ArkDatePicker.TableCellTrigger>
                                  </Table.Cell>
                                </ArkDatePicker.TableCell>
                              ))}
                            </Table.Row>
                          </ArkDatePicker.TableRow>
                        ))}
                      </Table.Body>
                    </ArkDatePicker.TableBody>
                  </Table.Root>
                </ArkDatePicker.Table>
              </>
            )}
          </ArkDatePicker.Context>
        </ArkDatePicker.View>

        <ArkDatePicker.View view="month">
          <ArkDatePicker.Context>
            {datePicker => (
              <>
                <ArkDatePicker.ViewControl asChild>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb="4">
                    <ArkDatePicker.PrevTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <LuChevronLeft />
                      </IconButton>
                    </ArkDatePicker.PrevTrigger>
                    <ArkDatePicker.ViewTrigger asChild>
                      <Button variant="ghost" size="sm" fontWeight="semibold">
                        <ArkDatePicker.RangeText />
                      </Button>
                    </ArkDatePicker.ViewTrigger>
                    <ArkDatePicker.NextTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <LuChevronRight />
                      </IconButton>
                    </ArkDatePicker.NextTrigger>
                  </Box>
                </ArkDatePicker.ViewControl>
                <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="2">
                  {datePicker.getMonthsGrid({columns: 4, format: 'short'}).map((months, id) => (
                    <Box key={`${id}`} display="contents">
                      {months.map((month, id) => (
                        <ArkDatePicker.TableCell key={`${id}`} value={month.value}>
                          <ArkDatePicker.TableCellTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              w="full"
                              _selected={{
                                bg: 'colorPalette.solid',
                                color: 'white',
                              }}
                              p="0.5"
                            >
                              {month.label}
                            </Button>
                          </ArkDatePicker.TableCellTrigger>
                        </ArkDatePicker.TableCell>
                      ))}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ArkDatePicker.Context>
        </ArkDatePicker.View>

        <ArkDatePicker.View view="year">
          <ArkDatePicker.Context>
            {datePicker => (
              <>
                <ArkDatePicker.ViewControl asChild>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb="4">
                    <ArkDatePicker.PrevTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <LuChevronLeft />
                      </IconButton>
                    </ArkDatePicker.PrevTrigger>
                    <ArkDatePicker.ViewTrigger asChild>
                      <Button variant="ghost" size="sm" fontWeight="semibold">
                        <ArkDatePicker.RangeText />
                      </Button>
                    </ArkDatePicker.ViewTrigger>
                    <ArkDatePicker.NextTrigger asChild>
                      <IconButton variant="ghost" size="sm">
                        <LuChevronRight />
                      </IconButton>
                    </ArkDatePicker.NextTrigger>
                  </Box>
                </ArkDatePicker.ViewControl>
                <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap="2">
                  {datePicker.getYearsGrid({columns: 4}).map((years, id) => (
                    <Box key={`${id}`} display="contents">
                      {years.map((year, id) => (
                        <ArkDatePicker.TableCell key={`${id}`} value={year.value}>
                          <ArkDatePicker.TableCellTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              w="full"
                              _selected={{
                                bg: 'colorPalette.solid',
                                color: 'white',
                              }}
                            >
                              {year.label}
                            </Button>
                          </ArkDatePicker.TableCellTrigger>
                        </ArkDatePicker.TableCell>
                      ))}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ArkDatePicker.Context>
        </ArkDatePicker.View>

        <Flex direction="column" mt="8" gap={3}>
          {rangeSelect ? (
            <HStack gap="2">
              <TimeInput label={t('start')} value={startTime} onChange={setStartTime} />
              <TimeInput label={t('end')} value={endTime} onChange={setEndTime} />
            </HStack>
          ) : (
            <Box>
              <TimeInput label={t('time')} value={startTime} onChange={setStartTime} />
            </Box>
          )}

          <Button w="full" colorPalette="cyan" onClick={handleApply} disabled={selectedDates.length === 0}>
            {t('apply')}
          </Button>
        </Flex>
      </Box>
    </ArkDatePicker.Root>
  )
}

function TimeInput({label, value, onChange}: {label: string; value: string; onChange: (value: string) => void}) {
  return (
    <Box flex="1">
      <Text fontSize="xs" fontWeight="medium" mb="1" color="fg.muted">
        {label}
      </Text>
      <Input type="time" value={value} onChange={e => onChange(e.target.value)} size="sm" />
    </Box>
  )
}

function dateValueToTimestamp(dateValue: DateValue, timeString: string): number {
  const date = dateValue.toDate(localTimeZone)
  const [hours, minutes] = timeString.split(':').map(Number)

  date.setHours(hours, minutes, 0, 0)

  return Math.floor(date.getTime() / 1000)
}

function getFormattedTime(timestamp: number) {
  const date = new Date(timestamp * 1000)

  return date.toLocaleTimeString(Tolgee.locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}
